import { useState, useCallback, useEffect } from 'react';
import { proveedorAdminService } from '../services/proveedorAdminService';
import { ProveedorDetalle, ContactoProveedorInput } from '../types';

export interface EmpresaProveedorForm {
  razonSocial: string;
  nit: string;
  departamentoId: number | null;
  ciudadId: number | null;
  direccion: string;
}

export interface ContactoProveedorForm {
  id: string;
  nombres: string;
  apellidos: string;
  cargo: string;
  telefono: string;
  correoElectronico: string;
  esPrincipal: boolean;
}

export interface EmpresaProveedorErrors {
  razonSocial?: string;
  nit?: string;
  departamentoId?: string;
  ciudadId?: string;
}

export interface ContactoProveedorErrors {
  nombres?: string;
  apellidos?: string;
  telefono?: string;
  correoElectronico?: string;
}

const EMPRESA_INICIAL: EmpresaProveedorForm = {
  razonSocial: '',
  nit: '',
  departamentoId: null,
  ciudadId: null,
  direccion: '',
};

function generarId(): string {
  return `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function validarEmpresa(empresa: EmpresaProveedorForm): EmpresaProveedorErrors {
  const errors: EmpresaProveedorErrors = {};
  if (!empresa.razonSocial.trim() || empresa.razonSocial.trim().length < 3) {
    errors.razonSocial = 'La razón social debe tener al menos 3 caracteres.';
  }
  if (!empresa.nit.trim()) {
    errors.nit = 'El NIT es requerido.';
  } else if (!/^[0-9]{8,15}$/.test(empresa.nit.trim())) {
    errors.nit = 'NIT inválido (solo dígitos, 8-15).';
  }
  return errors;
}

function validarContacto(contacto: Omit<ContactoProveedorForm, 'id'>): ContactoProveedorErrors {
  const errors: ContactoProveedorErrors = {};
  if (!contacto.nombres.trim()) errors.nombres = 'Ingresa los nombres.';
  if (!contacto.apellidos.trim()) errors.apellidos = 'Ingresa los apellidos.';
  if (!contacto.telefono.trim()) errors.telefono = 'Ingresa un teléfono.';
  if (!contacto.correoElectronico.trim()) {
    errors.correoElectronico = 'Ingresa un correo electrónico.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contacto.correoElectronico.trim())) {
    errors.correoElectronico = 'Correo electrónico inválido.';
  }
  return errors;
}

export function useNuevoProveedor(
  open: boolean,
  onClose: () => void,
  onGuardado?: (proveedor?: ProveedorDetalle) => void,
  proveedorEditar?: ProveedorDetalle | null
) {
  const [empresa, setEmpresa] = useState<EmpresaProveedorForm>(EMPRESA_INICIAL);
  const [contactos, setContactos] = useState<ContactoProveedorForm[]>([]);
  const [empresaErrors, setEmpresaErrors] = useState<EmpresaProveedorErrors>({});
  const [contactoEditing, setContactoEditing] = useState<ContactoProveedorForm | null>(null);
  const [contactoErrors, setContactoErrors] = useState<ContactoProveedorErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hayCambios, setHayCambios] = useState(false);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (proveedorEditar) {
      setEmpresa({
        razonSocial: proveedorEditar.razonSocial,
        nit: proveedorEditar.nit,
        departamentoId: proveedorEditar.departamentoId ?? null,
        ciudadId: proveedorEditar.ciudadId ?? null,
        direccion: proveedorEditar.direccion ?? '',
      });
      setContactos(
        proveedorEditar.contactos.map((c) => ({
          id: c.id ?? generarId(),
          nombres: c.nombres,
          apellidos: c.apellidos,
          cargo: c.cargo ?? '',
          telefono: c.telefono,
          correoElectronico: c.correoElectronico,
          esPrincipal: c.esPrincipal ?? false,
        }))
      );
    } else {
      setEmpresa(EMPRESA_INICIAL);
      setContactos([]);
    }
    setEmpresaErrors({});
    setContactoEditing(null);
    setContactoErrors({});
    setSaveError(null);
    setHayCambios(false);
    setConfirmandoCierre(false);
  }, [open, proveedorEditar]);

  const updateEmpresa = useCallback((field: keyof EmpresaProveedorForm, value: unknown) => {
    setEmpresa((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'departamentoId') updated.ciudadId = null;
      return updated;
    });
    setHayCambios(true);
  }, []);

  const iniciarNuevoContacto = useCallback(() => {
    setContactoEditing({
      id: generarId(),
      nombres: '',
      apellidos: '',
      cargo: '',
      telefono: '',
      correoElectronico: '',
      esPrincipal: contactos.length === 0,
    });
    setContactoErrors({});
  }, [contactos.length]);

  const editarContacto = useCallback((id: string) => {
    const c = contactos.find((x) => x.id === id);
    if (c) { setContactoEditing({ ...c }); setContactoErrors({}); }
  }, [contactos]);

  const updateContactoEditing = useCallback((field: keyof ContactoProveedorForm, value: unknown) => {
    setContactoEditing((prev) => prev ? { ...prev, [field]: value } : null);
  }, []);

  const guardarContacto = useCallback(() => {
    if (!contactoEditing) return false;
    const errors = validarContacto(contactoEditing);
    if (Object.keys(errors).length > 0) { setContactoErrors(errors); return false; }

    setContactos((prev) => {
      const existe = prev.find((c) => c.id === contactoEditing.id);
      let list = existe
        ? prev.map((c) => c.id === contactoEditing.id ? contactoEditing : c)
        : [...prev, contactoEditing];
      if (contactoEditing.esPrincipal) {
        list = list.map((c) => ({ ...c, esPrincipal: c.id === contactoEditing.id }));
      }
      return list;
    });
    setContactoEditing(null);
    setContactoErrors({});
    setHayCambios(true);
    return true;
  }, [contactoEditing]);

  const cancelarContacto = useCallback(() => {
    setContactoEditing(null);
    setContactoErrors({});
  }, []);

  const eliminarContacto = useCallback((id: string) => {
    setContactos((prev) => prev.filter((c) => c.id !== id));
    setHayCambios(true);
  }, []);

  const marcarPrincipal = useCallback((id: string) => {
    setContactos((prev) => prev.map((c) => ({ ...c, esPrincipal: c.id === id })));
    setHayCambios(true);
  }, []);

  const guardar = useCallback(async () => {
    const errors = validarEmpresa(empresa);
    if (Object.keys(errors).length > 0) { setEmpresaErrors(errors); return; }
    if (contactos.length === 0) { setSaveError('Agrega al menos un contacto.'); return; }

    const payload: ContactoProveedorInput[] = contactos.map((c) => ({
      nombres: c.nombres,
      apellidos: c.apellidos,
      cargo: c.cargo || undefined,
      telefono: c.telefono,
      correoElectronico: c.correoElectronico,
      esPrincipal: c.esPrincipal,
    }));

    try {
      setSaving(true);
      setSaveError(null);
      let result: ProveedorDetalle;
      if (proveedorEditar) {
        result = await proveedorAdminService.actualizar(proveedorEditar.id, {
          razonSocial: empresa.razonSocial,
          nit: empresa.nit,
          departamentoId: empresa.departamentoId ?? undefined,
          ciudadId: empresa.ciudadId ?? undefined,
          direccion: empresa.direccion || undefined,
          contactos: payload,
        });
      } else {
        result = await proveedorAdminService.crear({
          razonSocial: empresa.razonSocial,
          nit: empresa.nit,
          departamentoId: empresa.departamentoId ?? undefined,
          ciudadId: empresa.ciudadId ?? undefined,
          direccion: empresa.direccion || undefined,
          contactos: payload,
        });
      }
      setHayCambios(false);
      onGuardado?.(result);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error guardando proveedor');
    } finally {
      setSaving(false);
    }
  }, [empresa, contactos, proveedorEditar, onClose, onGuardado]);

  const intentarCerrar = useCallback(() => {
    if (hayCambios) { setConfirmandoCierre(true); } else { onClose(); }
  }, [hayCambios, onClose]);

  const confirmarCierre = useCallback(() => {
    setConfirmandoCierre(false);
    onClose();
  }, [onClose]);

  const cancelarCierre = useCallback(() => setConfirmandoCierre(false), []);

  return {
    empresa,
    contactos,
    empresaErrors,
    contactoEditing,
    contactoErrors,
    saving,
    saveError,
    hayCambios,
    confirmandoCierre,
    updateEmpresa,
    iniciarNuevoContacto,
    editarContacto,
    updateContactoEditing,
    guardarContacto,
    cancelarContacto,
    eliminarContacto,
    marcarPrincipal,
    guardar,
    intentarCerrar,
    confirmarCierre,
    cancelarCierre,
  };
}
